﻿import { ofType } from 'redux-observable';
import type { Observable } from 'rxjs';
import { combineLatest, EMPTY, merge, of } from 'rxjs';
import { filter, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import type { Action } from 'state/actions';
import type { RoutePath } from 'state/router';
import type { Dispatchable, EpicWithThunk } from 'state/state.common';
import type { WorkspaceType } from 'state/workspaces/model';
import type { WorkspacePersistedTabsApplied, WorkspaceTabCreated } from 'state/workspaces/workspace.actions';
import { isWorkspaceReady } from 'state/workspaces/workspaces/workspaces.selector';

export interface AddTabToWorkspaceOptions<
  Trigger extends Action = Action,
  ReadyTrigger extends Action & { workspace: WorkspaceType } = WorkspacePersistedTabsApplied | WorkspaceTabCreated,
  KT extends Trigger['type'] = Trigger['type'],
  KRT extends ReadyTrigger['type'] = ReadyTrigger['type'],
> {
  targetWorkspace: WorkspaceType;
  triggers: readonly KT[];
  workspaceReadyTriggers: readonly KRT[];
  navigateRoute: { path: RoutePath; subRoutePath?: string };
  onReady: (triggerPayload: Trigger) => Dispatchable;
}

export function openTabInWorkspaceEpic<
  Trigger extends Action = Action,
  ReadyTrigger extends Action & { workspace: WorkspaceType } = WorkspacePersistedTabsApplied | WorkspaceTabCreated,
  KT extends Trigger['type'] = Trigger['type'],
  KRT extends ReadyTrigger['type'] = ReadyTrigger['type'],
>(options: AddTabToWorkspaceOptions<Trigger, ReadyTrigger, KT, KRT>): EpicWithThunk {
  return (action$, state$, { selectors: { getCurrentRoute }, navigateTo }) => {
    return action$.pipe(
      ofType<Action, KT, Trigger>(...options.triggers),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const currentRoute = getCurrentRoute(state);
        const isWorkspaceInitialized: boolean = isWorkspaceReady(state, options.targetWorkspace);
        const tabsInitialized$: Observable<boolean> = isWorkspaceInitialized
          ? of(true)
          : action$.pipe(
              ofType<Action, KRT, ReadyTrigger>(...options.workspaceReadyTriggers),
              filter(({ workspace }) => workspace === options.targetWorkspace),
              map(() => true),
              take(1),
            );

        const navigateToScreenSucceed$ =
          currentRoute === options.navigateRoute.path
            ? of(true)
            : action$.pipe(
                ofType('ROUTE_CHANGED'),
                filter(({ path }) => path === options.navigateRoute.path),
                take(1),
              );

        const navigateToWorkspace$: Observable<Dispatchable> = of(true).pipe(
          tap(() => {
            if (currentRoute !== options.navigateRoute.path) {
              navigateTo(options.navigateRoute.path);
            }
          }),
          mergeMap(() => EMPTY),
        );

        return merge(
          combineLatest([tabsInitialized$, navigateToScreenSucceed$]).pipe(
            map(() => {
              return options.onReady(action);
            }),
          ),
          navigateToWorkspace$,
        );
      }),
    );
  };
}
